import React, { useState } from 'react';
import styles from './AIWriting.module.css';

const TONES = [
  'Professional & Engaging',
  'Casual & Friendly',
  'Informative & Educational',
  'Persuasive & Sales-driven',
  'Storytelling',
];

const LANGUAGES = ['English', 'Bangla', 'English + Bangla Mix'];

function AIWritingModal({ open, onClose, onGenerated }) {
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState(TONES[0]);
  const [category, setCategory] = useState('');
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ tokens: 0, length: 0 });
  const [error, setError] = useState('');
  const [stage, setStage] = useState('');

  const fetchCoverImage = async (blogData) => {
    const token = localStorage.getItem('token');
    const uid = localStorage.getItem('id');

    try {
      setStage('Finding a cover image...');
      const kwList = keywords.split(',').map(k => k.trim()).filter(Boolean);

      const res = await fetch('https://api.ecomtechbd.com/blog/ai-cover-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token, uid },
        body: JSON.stringify({
          title: blogData.title,
          keywords: kwList,
          category: blogData.category,
        }),
      });

      if (!res.ok) return null;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let coverUrl = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress') {
              if (data.stage === 'searching') setStage('Searching for cover image...');
              else if (data.stage === 'downloading') setStage('Downloading image...');
              else if (data.stage === 'uploading') setStage('Uploading to CDN...');
            }
            if (data.type === 'complete' && data.success) {
              coverUrl = data.coverImage;
            }
          } catch {}
        }
      }
      return coverUrl;
    } catch {
      return null;
    }
  };

  const handleGenerate = async () => {
    if (!topic.trim()) { setError('Please enter a topic'); return; }
    setError('');
    setGenerating(true);
    setProgress({ tokens: 0, length: 0 });
    setStage('Connecting to AI...');

    const token = localStorage.getItem('token');
    const uid = localStorage.getItem('id');
    let blogData = null;
    let blogStats = null;

    try {
      // Step 1: Generate blog content
      const res = await fetch('https://api.ecomtechbd.com/blog/ai-generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token,
          uid,
        },
        body: JSON.stringify({
          topic: topic.trim(),
          keywords: keywords.split(',').map(k => k.trim()).filter(Boolean),
          tone,
          category: category.trim(),
          language,
        }),
      });

      if (!res.ok) throw new Error('Server error');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      setStage('AI is writing your blog post...');

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'progress') {
              setProgress({ tokens: data.tokens, length: data.length });
              if (data.tokens < 50) setStage('AI is thinking and planning...');
              else if (data.tokens < 200) setStage('Writing introduction and structure...');
              else if (data.tokens < 500) setStage('Developing main content...');
              else if (data.tokens < 800) setStage('Adding details and SEO optimization...');
              else setStage('Finalizing the blog post...');
            }

            if (data.type === 'complete') {
              if (data.success) {
                blogData = data.data;
                blogStats = data.stats;
              } else {
                setError(data.message || 'Generation failed. Please try again.');
                setGenerating(false);
                setStage('');
                return;
              }
            }
          } catch (e) {
            // skip malformed SSE
          }
        }
      }

      if (!blogData) {
        setError('No response from AI. Please try again.');
        setGenerating(false);
        setStage('');
        return;
      }

      // Step 2: Fetch cover image
      const coverImage = await fetchCoverImage(blogData);
      if (coverImage) {
        blogData.coverImage = coverImage;
      }

      // Step 3: Done — send to parent
      setStage('Done!');
      setTimeout(() => {
        onGenerated(blogData, blogStats);
        setGenerating(false);
        setTopic('');
        setKeywords('');
        setCategory('');
        setProgress({ tokens: 0, length: 0 });
        setStage('');
      }, 500);

    } catch (err) {
      setError('Failed to connect: ' + err.message);
      setGenerating(false);
      setStage('');
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={() => !generating && onClose()}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <span className={styles.aiIcon}>AI</span>
            <h2 className={styles.modalTitle}>AI Blog Writer</h2>
          </div>
          {!generating && (
            <button className={styles.closeBtn} onClick={onClose}>&times;</button>
          )}
        </div>

        {!generating ? (
          <div className={styles.modalBody}>
            <p className={styles.modalDesc}>
              Tell us what you want to write about and AI will generate a complete blog post with SEO optimization and a cover image.
            </p>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>What's the blog post about? *</label>
              <textarea
                className={styles.fieldTextarea}
                rows={3}
                value={topic}
                onChange={e => { setTopic(e.target.value); setError(''); }}
                placeholder="e.g. Top 10 fashion trends for summer 2026 in Bangladesh"
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Focus Keywords</label>
                <input
                  className={styles.fieldInput}
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  placeholder="summer fashion, trendy bags, women style"
                />
                <span className={styles.fieldHint}>Comma separated</span>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Category</label>
                <input
                  className={styles.fieldInput}
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  placeholder="e.g. Fashion, Tips, News"
                />
              </div>
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Tone</label>
                <select className={styles.fieldSelect} value={tone} onChange={e => setTone(e.target.value)}>
                  {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Language</label>
                <select className={styles.fieldSelect} value={language} onChange={e => setLanguage(e.target.value)}>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={onClose}>Cancel</button>
              <button className={styles.generateBtn} onClick={handleGenerate}>
                Generate Blog Post
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <div className={styles.progressWrap}>
              <div className={styles.progressSpinner}>
                <div className={styles.spinnerRing}></div>
                <span className={styles.spinnerIcon}>AI</span>
              </div>
              <h3 className={styles.progressTitle}>{stage}</h3>
              <div className={styles.progressStats}>
                <span>Tokens: {progress.tokens}</span>
                <span>|</span>
                <span>Characters: {progress.length.toLocaleString()}</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${Math.min(100, (progress.tokens / 10))}%` }}
                />
              </div>
              <p className={styles.progressHint}>
                This may take 2-5 minutes. Please don't close this window.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIWritingModal;
